import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  KeyIcon,
  EnvelopeIcon,
  LockClosedIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";
import { AuthAPI } from "../../api/auth";
import { auth, setupRecaptcha } from "../../lib/firebase";
import { signInWithPhoneNumber, ConfirmationResult } from "firebase/auth";

interface Step1Data {
  recipient: string;
}

interface Step2Data {
  otp: string;
  new_pin: string;
}

const ForgotPin: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [recipient, setRecipient] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);

  const otpInputRef = useRef<HTMLInputElement>(null);

  // Initialize reCAPTCHA
  useEffect(() => {
    try {
      setupRecaptcha("forgot-pin-recaptcha");
    } catch (err) {
      console.error("Firebase Recaptcha init error:", err);
    }
    
    // Cleanup
    return () => {
       if ((window as any).recaptchaVerifier) {
          try {
             (window as any).recaptchaVerifier.clear();
             (window as any).recaptchaVerifier = null;
          } catch (e) {
            console.warn('Recaptcha cleanup failed', e);
          }
       }
    };
  }, []);

  const {
    register: registerStep1,
    handleSubmit: handleSubmitStep1,
    watch: watchStep1,
    formState: { errors: errorsStep1 },
  } = useForm<Step1Data>();

  const recipientValue = watchStep1("recipient");

  const {
    register: registerStep2,
    handleSubmit: handleSubmitStep2,
    formState: { errors: errorsStep2 },
  } = useForm<Step2Data>();

  const onRequestOtp = async (data: Step1Data) => {
    setIsLoading(true);
    setError(null);
    try {
      // Backend OTP for Reset (works for both email and phone)
      await AuthAPI.requestOtp(data.recipient, 'reset_pin');
      setRecipient(data.recipient);
      setStep(2);
    } catch (err: any) {
      console.error("OTP Request Error:", err);
      setError(err.message || "Account not found or failed to send OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  const onReset = async (data: Step2Data) => {
    setIsLoading(true);
    setError(null);
    try {
      // Standard Backend Reset (works for both email and phone)
      await AuthAPI.resetPin({
        recipient,
        otp: data.otp,
        new_pin: data.new_pin,
      });

      setSuccess("PIN reset successfully! Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);

    } catch (err: any) {
      setError(err.message || "Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center py-6 px-4 sm:px-6 lg:px-8">
      {/* Hidden Recaptcha */}
      <div id="forgot-pin-recaptcha"></div>

      <div className="max-w-md w-full space-y-6 bg-white p-6 sm:p-8 rounded-2xl shadow-xl">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
            <KeyIcon className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="mt-4 text-2xl sm:text-3xl font-extrabold text-gray-900">
            Reset Your PIN
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {step === 1 ? "Enter your registered email or phone." : "Create a new secret PIN."}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm text-center">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm text-center">
            {success}
          </div>
        )}

        {step === 1 ? (
          <form onSubmit={handleSubmitStep1(onRequestOtp)} className="mt-8 space-y-5">
            <div>
              <label htmlFor="recipient" className="block text-sm font-medium text-gray-700 mb-1">
                Mobile Number or Email
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  {recipientValue?.includes('@') ? (
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <input
                  {...registerStep1("recipient", { required: "Email or Phone is required" })}
                  type="text"
                  className="pl-10 w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  placeholder="e.g., 9876543210"
                />
              </div>
              {errorsStep1.recipient && <p className="mt-1 text-xs text-red-500">{errorsStep1.recipient.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-md text-base font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all active:scale-95"
            >
              {isLoading ? "Checking..." : "Verify My Account"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmitStep2(onReset)} className="mt-6 space-y-5">
            <div className="text-center mb-4 bg-blue-50 py-2.5 rounded-xl border border-blue-100">
               <span className="text-sm text-gray-600">Code sent to <strong>{recipient}</strong></span>
               <button type="button" onClick={() => setStep(1)} className="ml-2 text-xs text-blue-600 font-bold hover:underline">Change</button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Enter 6-Digit OTP</label>
              <div className="relative">
                 <KeyIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                 <input
                  {...registerStep2("otp", { required: "OTP is required", minLength: 6 })}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 text-lg tracking-widest font-mono"
                  placeholder="000000"
                 />
              </div>
              {errorsStep2.otp && <p className="mt-1 text-xs text-red-500">{errorsStep2.otp.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Secret PIN</label>
              <div className="relative">
                 <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                 <input
                  {...registerStep2("new_pin", { 
                      required: "New PIN is required", 
                      minLength: { value: 4, message: "Min 4 digits" },
                      maxLength: { value: 6, message: "Max 6 digits" }
                  })}
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 text-lg tracking-widest font-mono"
                  placeholder="****"
                 />
              </div>
              {errorsStep2.new_pin && <p className="mt-1 text-xs text-red-500">{errorsStep2.new_pin.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-4 px-4 border border-transparent rounded-xl shadow-lg text-lg font-bold text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-all active:scale-95 mt-4"
            >
              {isLoading ? "Updating PIN..." : "Save New PIN"}
            </button>
          </form>
        )}

        <div className="text-center mt-4 border-t border-gray-100 pt-4">
            <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-500 text-sm flex items-center justify-center">
               Back to Secure Login
            </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPin;
